/**
 * NeoCoreCreditosOriginacionPage — bandeja de originación de crédito.
 *
 * Tres pipelines:
 *   1. PENDIENTES · solicitudes recién creadas, esperando aprobación humana.
 *      Acciones: aprobar, rechazar. El operador solo interviene manualmente
 *      en montos >Q1000 (los <=Q1000 se auto-aprueban por Hunter).
 *   2. APROBADAS SIN FIRMA · esperando que el asociado firme desde la app.
 *      Informativa · sin acciones del admin.
 *   3. FIRMADAS · desembolso AUTOMÁTICO. Cuando el asociado firma, el backend
 *      genera el PDF del pagaré y programa el desembolso a +30 min
 *      (solicitud_credito.desembolso_programado_at). Un cron
 *      (ejecutar_desembolsos_programados, cada 15 min) acredita el monto
 *      solo a la cuenta de ahorro interna. El operador NO desembolsa a mano
 *      en el caso normal — solo como fallback si el cron no corrió (la hora
 *      programada ya pasó y la solicitud sigue en 'aprobada'), o en solicitudes
 *      viejas pre-flujo sin desembolso_programado_at.
 *
 * RPCs consumidas (todas requieren rol admin):
 *   - aprobar_solicitud_credito_admin(uuid, text)
 *   - rechazar_solicitud_credito_admin(uuid, text)
 *   - desembolsar_credito_admin(jsonb) — protegido a nivel DB (exige
 *     estado='aprobada' + row lock, sin riesgo de doble desembolso). Se usa
 *     solo como fallback manual; el flujo normal lo corre el cron.
 *
 * El PDF del pagaré ya se genera al firmar (no se pega URL manual salvo en el
 * edge case en que la generación client-side falló).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowsClockwise,
  CheckCircle,
  FileText,
  HandCoins,
  Warning,
  XCircle,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { regenerarPagare } from '@/lib/regenerarPagare'

/* ═══════════════════════════════════════════════════════════════
   Tipos
   ═══════════════════════════════════════════════════════════════ */

type Tier = 'basico' | 'plus' | 'pro' | 'premium' | 'black'

type SolicitudRow = {
  id: string
  asociado_id: string
  asociado_nombre: string
  asociado_neo_id: string
  tipo_nombre: string
  tipo_codigo: string
  monto: number
  plazo_dias: number
  proposito: string | null
  estado: 'pendiente' | 'aprobada' | 'rechazada' | 'desembolsada' | 'cancelada'
  created_at: string
  aprobada_at: string | null
  aprobacion_auto: boolean
  firma_timestamp: string | null
  firma_paths_count: number
  pagare_pdf_url: string | null   // path en bucket 'pagares' (ej. asociadoId/solicitudId.pdf)
  desembolso_programado_at: string | null  // ISO · cuándo el cron desembolsa (firma +30 min). null = solicitud vieja pre-flujo.
  // ── Hunter context ────────────────────────────────────────
  tier_actual: Tier | null
  tier_max: Tier | null
  retos_activos: number
  telefono_verificado_at: string | null
  otp_verificado_at: string | null
  // ── Historial crediticio ──────────────────────────────────
  creditos_previos: number    // total (excluida esta solicitud)
  creditos_al_dia: number     // pagados sin mora
  creditos_en_mora: number    // con historial de mora
}

// Monto máximo por tier · fuente de verdad: credito_nivel (DB). Mantener en
// sync con la tabla; hoy: basico 500, plus 700, pro 1200, premium 1800, black 2500.
const TIER_MONTO: Record<Tier, number> = {
  basico: 500, plus: 700, pro: 1200, premium: 1800, black: 2500,
}
const TIER_LABEL: Record<Tier, string> = {
  basico: 'Básico', plus: 'Plus', pro: 'Pro', premium: 'Premium', black: 'Black',
}
// Color por tier · para el chip visual
const TIER_COLOR: Record<Tier, string> = {
  basico: '#94A3B8', plus: '#EA580C', pro: '#DC2626', premium: '#7C3AED', black: '#1F2937',
}

type Bandeja = 'pendientes' | 'aprobadas_sin_firma' | 'firmadas'

type Toast =
  | { tono: 'ok'; titulo: string; detalle: string }
  | { tono: 'err'; titulo: string; detalle: string }

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

const fmtQ = (n: number) =>
  'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-GT', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Guatemala',
  })
}

/** Hora HH:MM de un instante ISO, en zona horaria de Guatemala. */
const fmtHora = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-GT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Guatemala',
  })

/**
 * ¿Una solicitud firmada necesita intervención manual del operador?
 *
 * Flujo nuevo: al firmar, el backend programa el desembolso a +30 min y un cron
 * (ejecutar_desembolsos_programados, cada 15 min) lo ejecuta solo. El operador
 * solo actúa como fallback:
 *   - desembolso_programado_at FUTURO → NO (el cron la va a agarrar). Informativo.
 *   - desembolso_programado_at VENCIDO y sigue 'aprobada' → SÍ (el cron no corrió).
 *   - desembolso_programado_at null (solicitud vieja pre-flujo) → SÍ (desembolso legacy).
 */
function requiereDesembolsoManual(r: SolicitudRow): boolean {
  if (!r.desembolso_programado_at) return true // legacy · pre-flujo automático
  return new Date(r.desembolso_programado_at).getTime() < Date.now()
}

/**
 * Días transcurridos desde la fecha ISO. Devuelve entero (piso).
 * Se usa para el chip de "esperando X días" en la cola de originación.
 */
function diasDesde(iso: string | null): number {
  if (!iso) return 0
  const d = new Date(iso)
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)))
}

/**
 * Chip visual de urgencia · basado en días esperando.
 *   0 días  → gris   ("hoy")
 *   1 día   → gris   ("ayer")
 *   2 días  → amarillo (empieza a preocupar)
 *   3+ días → naranja (llamar la atención)
 */
function EsperaChip({ iso }: { iso: string | null }) {
  const dias = diasDesde(iso)
  const label =
    dias === 0 ? 'hoy'
    : dias === 1 ? 'ayer'
    : `${dias}d`
  const tono =
    dias >= 3 ? { bg: 'rgba(234,88,12,0.10)', color: '#9A3412', border: 'rgba(234,88,12,0.28)' }
    : dias >= 2 ? { bg: 'rgba(202,138,4,0.10)', color: '#854D0E', border: 'rgba(202,138,4,0.28)' }
    : { bg: 'rgba(100,116,139,0.10)', color: '#475569', border: 'rgba(100,116,139,0.20)' }
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      background: tono.bg,
      color: tono.color,
      border: `1px solid ${tono.border}`,
      marginLeft: 8,
    }}>
      {label}
    </span>
  )
}

/* ═══════════════════════════════════════════════════════════════
   Componente principal
   ═══════════════════════════════════════════════════════════════ */

export default function NeoCoreCreditosOriginacionPage() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<SolicitudRow[]>([])
  const [bandeja, setBandeja] = useState<Bandeja>('pendientes')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<Toast | null>(null)

  // Modals
  const [aprobarModal, setAprobarModal] = useState<SolicitudRow | null>(null)
  const [rechazarModal, setRechazarModal] = useState<SolicitudRow | null>(null)
  const [desembolsarModal, setDesembolsarModal] = useState<SolicitudRow | null>(null)

  const showToast = useCallback((t: Toast) => {
    setToast(t)
    window.setTimeout(() => setToast(null), 5000)
  }, [])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const { data, error: err } = await supabase
        .from('solicitud_credito')
        .select(
          `
          id,
          monto_solicitado,
          plazo_dias,
          proposito,
          estado,
          created_at,
          aprobada_at,
          aprobacion_auto,
          firma_timestamp,
          firma_paths,
          pagare_pdf_url,
          desembolso_programado_at,
          otp_verificado_at,
          asociado_id,
          asociado:asociado_id ( id, nombre_completo, neo_id, tier_actual, tier_max_alcanzado, telefono_verificado_at ),
          tipo_credito:tipo_credito_id ( codigo, nombre )
        `,
        )
        .in('estado', ['pendiente', 'aprobada'])
        .order('created_at', { ascending: false })

      if (err) throw err

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawRows = (data ?? []) as any[]

      // Traer contadores Hunter por asociado en paralelo · una query por asociado
      // pero todas en paralelo (batch de <= 10 usualmente). Alternativa futura:
      // vista materializada. Para MVP con volumen chico esto está bien.
      const asociadoIds = Array.from(new Set(rawRows.map((r) => r.asociado_id)))

      const [retosByAsociado, creditosByAsociado] = await Promise.all([
        // Retos activos por asociado
        supabase
          .from('credito_reto')
          .select('asociado_id')
          .in('asociado_id', asociadoIds)
          .eq('estado', 'activo'),
        // Créditos previos por asociado con conteo por estado
        supabase
          .from('credito')
          .select('asociado_id, estado')
          .in('asociado_id', asociadoIds),
      ])

      const retosMap = new Map<string, number>()
      for (const r of retosByAsociado.data ?? []) {
        retosMap.set(r.asociado_id, (retosMap.get(r.asociado_id) ?? 0) + 1)
      }

      const credMap = new Map<string, { total: number; alDia: number; mora: number }>()
      for (const c of creditosByAsociado.data ?? []) {
        const cur = credMap.get(c.asociado_id) ?? { total: 0, alDia: 0, mora: 0 }
        cur.total += 1
        if (c.estado === 'al_dia' || c.estado === 'cancelado' || c.estado === 'cancelado_anticipado') cur.alDia += 1
        if (c.estado === 'en_mora' || c.estado === 'castigado') cur.mora += 1
        credMap.set(c.asociado_id, cur)
      }

      const mapped: SolicitudRow[] = rawRows.map((r) => {
        const cred = credMap.get(r.asociado_id) ?? { total: 0, alDia: 0, mora: 0 }
        return {
          id: r.id,
          asociado_id: r.asociado_id,
          asociado_nombre: r.asociado?.nombre_completo ?? '—',
          asociado_neo_id: r.asociado?.neo_id ?? '',
          tipo_nombre: r.tipo_credito?.nombre ?? '—',
          tipo_codigo: r.tipo_credito?.codigo ?? '',
          monto: Number(r.monto_solicitado),
          plazo_dias: r.plazo_dias,
          proposito: r.proposito,
          estado: r.estado,
          created_at: r.created_at,
          aprobada_at: r.aprobada_at,
          aprobacion_auto: r.aprobacion_auto ?? false,
          firma_timestamp: r.firma_timestamp,
          firma_paths_count: Array.isArray(r.firma_paths) ? r.firma_paths.length : 0,
          pagare_pdf_url: r.pagare_pdf_url ?? null,
          desembolso_programado_at: r.desembolso_programado_at ?? null,
          tier_actual: r.asociado?.tier_actual ?? null,
          tier_max: r.asociado?.tier_max_alcanzado ?? null,
          retos_activos: retosMap.get(r.asociado_id) ?? 0,
          telefono_verificado_at: r.asociado?.telefono_verificado_at ?? null,
          otp_verificado_at: r.otp_verificado_at ?? null,
          creditos_previos: cred.total,
          creditos_al_dia: cred.alDia,
          creditos_en_mora: cred.mora,
        }
      })

      setRows(mapped)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  // Marca el número del asociado como verificado (ADR-019). Requisito para
  // desembolsar: sin verificar, el trigger bloquea el desembolso.
  const marcarVerificado = useCallback(async (solicitudId: string) => {
    const { error } = await supabase.rpc('verificar_desembolso_solicitud_admin', {
      p_solicitud_id: solicitudId, p_nota: 'Override de desembolso desde originación',
    })
    if (error) { showToast({ tono: 'err', titulo: 'No se pudo verificar', detalle: error.message }); return }
    showToast({ tono: 'ok', titulo: 'Crédito verificado', detalle: 'Ya se puede desembolsar.' })
    void fetchData()
  }, [fetchData, showToast])

  // Pendientes: más VIEJAS arriba (la que espera hace más días es prioridad).
  const pendientes = useMemo(
    () =>
      rows
        .filter((r) => r.estado === 'pendiente')
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [rows],
  )
  // Aprobadas sin firma: informativa · más recientes arriba (última actividad).
  const aprobadasSinFirma = useMemo(
    () =>
      rows
        .filter((r) => r.estado === 'aprobada' && r.firma_paths_count === 0)
        .sort((a, b) =>
          new Date(b.aprobada_at ?? b.created_at).getTime() -
          new Date(a.aprobada_at ?? a.created_at).getTime(),
        ),
    [rows],
  )
  // Firmadas: más VIEJAS arriba (desembolsar primero las que llevan más tiempo firmadas).
  const firmadas = useMemo(
    () =>
      rows
        .filter((r) => r.estado === 'aprobada' && r.firma_paths_count > 0)
        .sort((a, b) =>
          new Date(a.firma_timestamp ?? a.created_at).getTime() -
          new Date(b.firma_timestamp ?? b.created_at).getTime(),
        ),
    [rows],
  )

  // Firmadas que necesitan al operador · el cron no las agarró (vencidas) o son
  // legacy sin desembolso programado. Las de desembolso automático futuro NO
  // cuentan como acción requerida — el cron las maneja.
  const firmadasFallback = useMemo(
    () => firmadas.filter(requiereDesembolsoManual),
    [firmadas],
  )

  const currentList =
    bandeja === 'pendientes'
      ? pendientes
      : bandeja === 'aprobadas_sin_firma'
        ? aprobadasSinFirma
        : firmadas

  /* ═══════════════════════════════════════════════════════════════
     Acciones
     ═══════════════════════════════════════════════════════════════ */

  const handleAprobar = async (s: SolicitudRow, nota: string) => {
    try {
      setBusy(s.id)
      const { error: err } = await supabase.rpc('aprobar_solicitud_credito_admin', {
        p_solicitud_id: s.id,
        p_nota: nota || null,
      })
      if (err) throw err
      setAprobarModal(null)
      showToast({
        tono: 'ok',
        titulo: 'Solicitud aprobada',
        detalle: `${s.asociado_nombre} · ${fmtQ(s.monto)} · esperando firma en la app.`,
      })
      await fetchData()
    } catch (e) {
      showToast({
        tono: 'err',
        titulo: 'No pudimos aprobar',
        detalle: e instanceof Error ? e.message : 'Error desconocido',
      })
    } finally {
      setBusy(null)
    }
  }

  const handleRechazar = async (s: SolicitudRow, motivo: string) => {
    try {
      setBusy(s.id)
      const { error: err } = await supabase.rpc('rechazar_solicitud_credito_admin', {
        p_solicitud_id: s.id,
        p_motivo: motivo,
      })
      if (err) throw err
      setRechazarModal(null)
      showToast({
        tono: 'ok',
        titulo: 'Solicitud rechazada',
        detalle: `${s.asociado_nombre} · ${fmtQ(s.monto)} · el asociado verá el motivo en la app.`,
      })
      await fetchData()
    } catch (e) {
      showToast({
        tono: 'err',
        titulo: 'No pudimos rechazar',
        detalle: e instanceof Error ? e.message : 'Error desconocido',
      })
    } finally {
      setBusy(null)
    }
  }

  const handleDesembolsar = async (s: SolicitudRow, pagareUrl: string) => {
    try {
      setBusy(s.id)
      const { error: err } = await supabase.rpc('desembolsar_credito_admin', {
        p_input: {
          solicitud_id: s.id,
          pagare_pdf_url: pagareUrl,
        },
      })
      if (err) throw err
      setDesembolsarModal(null)
      showToast({
        tono: 'ok',
        titulo: 'Desembolso registrado',
        detalle: `${s.asociado_nombre} · ${fmtQ(s.monto)} acreditado a su cuenta de ahorro.`,
      })
      await fetchData()
    } catch (e) {
      showToast({
        tono: 'err',
        titulo: 'No pudimos desembolsar',
        detalle: e instanceof Error ? e.message : 'Error desconocido',
      })
    } finally {
      setBusy(null)
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════ */

  return (
    <div style={S.page}>
      {/* Toast */}
      {toast && (
        <div style={{ ...S.toast, ...(toast.tono === 'ok' ? S.toastOk : S.toastErr) }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            {toast.tono === 'ok' ? (
              <CheckCircle size={18} weight="fill" style={{ marginTop: 2 }} />
            ) : (
              <Warning size={18} weight="fill" style={{ marginTop: 2 }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{toast.titulo}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{toast.detalle}</div>
            </div>
            <button
              onClick={() => setToast(null)}
              style={{
                background: 'transparent', border: 'none', color: 'inherit',
                cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0,
              }}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={S.headerRow}>
        <div>
          <div style={{ marginBottom: 4 }}>
            <Link to="/neocore/creditos" style={S.crumb}>← Cartera</Link>
          </div>
          <h1 style={S.h1}>Originación · pipeline Hunter</h1>
          <p style={S.subtitle}>
            Cola priorizada por tiempo de espera. Cada card muestra el tier del asociado y su historial.
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{ ...S.btnGhost, cursor: loading ? 'wait' : 'pointer' }}
        >
          <ArrowsClockwise size={14} weight="bold" />
          Actualizar
        </button>
      </div>

      {/* Métricas rápidas · lo que necesita el operador de un vistazo */}
      <div style={S.metricasRow}>
        <MetricaTile
          label="Acción requerida"
          valor={pendientes.length + firmadasFallback.length}
          detalle={`${pendientes.length} por aprobar · ${firmadasFallback.length} por desembolsar`}
          tono="warn"
        />
        <MetricaTile
          label="Esperando asociado"
          valor={aprobadasSinFirma.length}
          detalle="Firma pendiente en la app"
          tono="neutral"
        />
        <MetricaTile
          label="Monto en pipeline"
          valor={fmtQ(rows.reduce((s, r) => s + r.monto, 0))}
          detalle={`${rows.length} solicitudes activas`}
          tono="brand"
        />
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        <Tab
          active={bandeja === 'pendientes'}
          onClick={() => setBandeja('pendientes')}
          label="Pendientes de aprobación"
          count={pendientes.length}
          tono="warning"
        />
        <Tab
          active={bandeja === 'aprobadas_sin_firma'}
          onClick={() => setBandeja('aprobadas_sin_firma')}
          label="Esperando firma"
          count={aprobadasSinFirma.length}
          tono="neutral"
        />
        <Tab
          active={bandeja === 'firmadas'}
          onClick={() => setBandeja('firmadas')}
          label="Firmadas · desembolso automático"
          count={firmadas.length}
          tono="success"
        />
      </div>

      {/* Error global */}
      {error && (
        <div style={S.banner('err')}>
          <Warning size={16} weight="bold" />
          <div>
            <div style={{ fontWeight: 600 }}>No pudimos cargar la bandeja</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>{error}</div>
          </div>
        </div>
      )}

      {/* Contenido */}
      {loading ? (
        <div style={S.emptyPanel}>
          <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>Cargando…</div>
        </div>
      ) : currentList.length === 0 ? (
        <EmptyState bandeja={bandeja} />
      ) : (
        <div style={S.grid}>
          {currentList.map((r) => (
            <SolicitudHunterCard
              key={r.id}
              r={r}
              bandeja={bandeja}
              busy={busy === r.id}
              onAprobar={() => setAprobarModal(r)}
              onRechazar={() => setRechazarModal(r)}
              onCancelar={() => setRechazarModal(r)}
              onDesembolsar={() => setDesembolsarModal(r)}
              onVerificar={() => void marcarVerificado(r.id)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {aprobarModal && (
        <AprobarModal
          solicitud={aprobarModal}
          onClose={() => setAprobarModal(null)}
          onConfirm={handleAprobar}
          busy={busy === aprobarModal.id}
        />
      )}
      {rechazarModal && (
        <RechazarModal
          solicitud={rechazarModal}
          onClose={() => setRechazarModal(null)}
          onConfirm={handleRechazar}
          busy={busy === rechazarModal.id}
        />
      )}
      {desembolsarModal && (
        <DesembolsarModal
          solicitud={desembolsarModal}
          onClose={() => setDesembolsarModal(null)}
          onConfirm={handleDesembolsar}
          busy={busy === desembolsarModal.id}
        />
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════
   Sub-componentes
   ═══════════════════════════════════════════════════════════════ */

function Tab({
  active,
  onClick,
  label,
  count,
  tono,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number
  tono: 'warning' | 'success' | 'neutral'
}) {
  const dotColor =
    tono === 'warning'
      ? 'var(--warning, #d97706)'
      : tono === 'success'
        ? 'var(--ok-ink, #047857)'
        : 'var(--ink-soft, #4b5563)'
  const dotBg =
    tono === 'warning'
      ? 'rgba(217,119,6,0.10)'
      : tono === 'success'
        ? 'rgba(16,185,129,0.12)'
        : 'rgba(75,85,99,0.10)'
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        fontSize: 14,
        fontWeight: 500,
        background: 'transparent',
        color: active ? 'var(--ink)' : 'var(--ink-soft)',
        border: 'none',
        borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
        marginBottom: -1,
        cursor: 'pointer',
        transition: 'all 160ms ease',
        fontFamily: 'inherit',
      }}
    >
      {label}
      {count > 0 && (
        <span
          style={{
            background: dotBg,
            color: dotColor,
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {count}
        </span>
      )}
    </button>
  )
}

// Th/Td removidos · la tabla se reemplazó por SolicitudHunterCard (grid de cards).

function ActionBtn({
  onClick,
  disabled,
  tono,
  icon,
  label,
}: {
  onClick: () => void
  disabled: boolean
  tono: 'success' | 'danger' | 'primary'
  icon: React.ReactNode
  label: string
}) {
  const bg =
    tono === 'success'
      ? 'var(--ok-ink, #047857)'
      : tono === 'danger'
        ? 'var(--danger, #a32d2d)'
        : 'var(--brand)'
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 600,
        background: bg,
        color: '#fff',
        border: 'none',
        borderRadius: 'var(--radius-sm, 6px)',
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        boxShadow: 'var(--shadow-sm)',
        fontFamily: 'inherit',
      }}
    >
      {icon}
      {label}
    </button>
  )
}

function EmptyState({ bandeja }: { bandeja: Bandeja }) {
  const EMPTY: Record<Bandeja, { title: string; detail: string }> = {
    pendientes: {
      title: 'Sin solicitudes por revisar',
      detail:
        'Cuando un asociado solicite crédito desde la app aparecerá acá para que lo aprobés o rechacés.',
    },
    aprobadas_sin_firma: {
      title: 'Nadie está esperando firmar',
      detail:
        'Todos los asociados con solicitud aprobada ya firmaron su pagaré desde la app móvil.',
    },
    firmadas: {
      title: 'Sin créditos firmados',
      detail:
        'Cuando el asociado firme, su crédito se acredita solo en unos minutos. Acá ves el estado.',
    },
  }
  const copy = EMPTY[bandeja]

  return (
    <div style={S.emptyPanel}>
      <FileText size={32} color="var(--muted-foreground)" style={{ marginBottom: 10 }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{copy.title}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 4, maxWidth: 380 }}>
        {copy.detail}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════
   Modals
   ═══════════════════════════════════════════════════════════════ */

function ModalWrap({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={S.modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={S.modalCard}>
        {children}
      </div>
    </div>
  )
}

function ResumenSolicitud({ s }: { s: SolicitudRow }) {
  return (
    <div style={S.resumenGrid}>
      <ResumenCell label="Asociado" valor={`${s.asociado_nombre} · neoID ${s.asociado_neo_id}`} />
      <ResumenCell label="Producto" valor={s.tipo_nombre} />
      <ResumenCell label="Monto" valor={fmtQ(s.monto)} strong />
      <ResumenCell label="Plazo" valor={`${s.plazo_dias} días`} />
      {s.proposito && <ResumenCell label="Destino declarado" valor={s.proposito} full />}
      <ResumenCell label="Solicitado" valor={fmtFecha(s.created_at)} full />
    </div>
  )
}

function ResumenCell({
  label,
  valor,
  strong,
  full,
}: {
  label: string
  valor: string
  strong?: boolean
  full?: boolean
}) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginBottom: 2, fontWeight: 500 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--ink)',
          fontWeight: strong ? 700 : 500,
        }}
      >
        {valor}
      </div>
    </div>
  )
}

function AprobarModal({
  solicitud,
  onClose,
  onConfirm,
  busy,
}: {
  solicitud: SolicitudRow
  onClose: () => void
  onConfirm: (s: SolicitudRow, nota: string) => void
  busy: boolean
}) {
  const [nota, setNota] = useState('')
  return (
    <ModalWrap onClose={onClose}>
      <div style={S.modalHead}>
        <div style={{ ...S.modalIcon, background: 'rgba(16,185,129,0.14)', color: 'var(--ok-ink, #047857)' }}>
          <CheckCircle size={20} weight="bold" />
        </div>
        <div>
          <h2 style={S.modalTitle}>Aprobar solicitud</h2>
          <p style={S.modalSub}>Se le va a pedir al asociado que firme el pagaré desde la app.</p>
        </div>
      </div>

      <ResumenSolicitud s={solicitud} />

      <label style={{ display: 'block', margin: '18px 0 20px' }}>
        <span style={S.label}>Nota interna (opcional)</span>
        <textarea
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          rows={2}
          placeholder="Ej: verificado ingresos con recibo de sueldo…"
          style={S.textarea}
        />
        <div style={S.hint}>Solo la ve el equipo. Queda en la bitácora del crédito.</div>
      </label>

      <div style={S.modalActions}>
        <button onClick={onClose} disabled={busy} style={S.btnGhost}>
          Cancelar
        </button>
        <button
          onClick={() => onConfirm(solicitud, nota)}
          disabled={busy}
          style={{ ...S.btnPrimary, background: 'var(--ok-ink, #047857)' }}
        >
          {busy ? 'Aprobando…' : 'Aprobar solicitud'}
        </button>
      </div>
    </ModalWrap>
  )
}

function RechazarModal({
  solicitud,
  onClose,
  onConfirm,
  busy,
}: {
  solicitud: SolicitudRow
  onClose: () => void
  onConfirm: (s: SolicitudRow, motivo: string) => void
  busy: boolean
}) {
  const [motivo, setMotivo] = useState('')
  const canSubmit = motivo.trim().length >= 8
  const esCancelacion = solicitud.estado === 'aprobada'
  return (
    <ModalWrap onClose={onClose}>
      <div style={S.modalHead}>
        <div style={{ ...S.modalIcon, background: 'rgba(163,45,45,0.12)', color: 'var(--danger, #a32d2d)' }}>
          <XCircle size={20} weight="bold" />
        </div>
        <div>
          <h2 style={S.modalTitle}>
            {esCancelacion ? 'Cancelar solicitud aprobada' : 'Rechazar solicitud'}
          </h2>
          <p style={S.modalSub}>
            {esCancelacion
              ? 'La solicitud ya fue aprobada. Al cancelar, el asociado verá el motivo en la app y podrá volver a solicitar.'
              : 'El asociado va a ver el motivo en su app y podrá pedir otra vez cuando resuelva.'}
          </p>
        </div>
      </div>

      <ResumenSolicitud s={solicitud} />

      <label style={{ display: 'block', margin: '18px 0 20px' }}>
        <span style={S.label}>
          Motivo <span style={{ color: 'var(--danger)' }}>*</span>
        </span>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          required
          placeholder={
            esCancelacion
              ? 'Ej: el asociado cambió de opinión, error del operador al aprobar, prueba UX…'
              : 'Ej: el monto excede su capacidad de pago según recibos de sueldo, falta comprobar dirección…'
          }
          style={S.textarea}
        />
        <div style={S.hint}>
          Mínimo 8 caracteres. Sé claro — el asociado lo va a leer.
        </div>
      </label>

      <div style={S.modalActions}>
        <button onClick={onClose} disabled={busy} style={S.btnGhost}>
          Volver
        </button>
        <button
          onClick={() => onConfirm(solicitud, motivo)}
          disabled={busy || !canSubmit}
          style={{
            ...S.btnPrimary,
            background: 'var(--danger, #a32d2d)',
            opacity: canSubmit ? 1 : 0.55,
            cursor: busy || !canSubmit ? 'not-allowed' : 'pointer',
          }}
        >
          {busy
            ? (esCancelacion ? 'Cancelando…' : 'Rechazando…')
            : (esCancelacion ? 'Cancelar esta solicitud' : 'Rechazar solicitud')}
        </button>
      </div>
    </ModalWrap>
  )
}

function DesembolsarModal({
  solicitud,
  onClose,
  onConfirm,
  busy,
}: {
  solicitud: SolicitudRow
  onClose: () => void
  onConfirm: (s: SolicitudRow, pagareUrl: string) => void
  busy: boolean
}) {
  // Si el asociado ya subió el PDF (flujo nuevo), la URL viene del backend.
  // El operador solo lo revisa y confirma. Si falta (edge case: falló la
  // generación client-side), fallback a URL manual.
  const pagareYaSubidoOrig = !!solicitud.pagare_pdf_url
  const [pagareUrlManual, setPagareUrlManual] = useState('')
  const [confirmoRevision, setConfirmoRevision] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  // Regeneración del pagaré desde la firma guardada (cuando la subida
  // client-side falló). Al regenerar, el server ya tiene pagare_pdf_url,
  // así que lo tratamos como "ya subido".
  const [regenerado, setRegenerado] = useState(false)
  const [regenerando, setRegenerando] = useState(false)
  const [regenError, setRegenError] = useState<string | null>(null)
  const [mostrarManual, setMostrarManual] = useState(false)
  const pagareYaSubido = pagareYaSubidoOrig || regenerado

  const onRegenerar = async () => {
    setRegenerando(true); setRegenError(null)
    const res = await regenerarPagare(solicitud.id)
    if (!res.ok) { setRegenError(res.error); setRegenerando(false); return }
    // Preview del PDF recién subido
    const { data } = await supabase.storage.from('pagares').createSignedUrl(res.url, 600)
    setPreviewUrl(data?.signedUrl ?? null)
    setRegenerado(true)
    setRegenerando(false)
  }

  // Genera un signed URL temporal (10 min) para que el operador pueda abrir el PDF
  useEffect(() => {
    if (!pagareYaSubido || !solicitud.pagare_pdf_url) return
    setPreviewLoading(true)
    supabase.storage
      .from('pagares')
      .createSignedUrl(solicitud.pagare_pdf_url, 600)
      .then(({ data, error }) => {
        setPreviewLoading(false)
        if (error) {
          console.error('[preview] signed url error', error)
          return
        }
        setPreviewUrl(data?.signedUrl ?? null)
      })
  }, [pagareYaSubido, solicitud.pagare_pdf_url])

  const canSubmit = pagareYaSubido
    ? confirmoRevision
    : pagareUrlManual.trim().length > 0

  const urlAEnviar = pagareYaSubido ? '' : pagareUrlManual
  // Si viene del backend, el RPC lo toma solo · pasamos '' y el server usa solicitud.pagare_pdf_url

  return (
    <ModalWrap onClose={onClose}>
      <div style={S.modalHead}>
        <div style={{ ...S.modalIcon, background: 'var(--brand-soft, #ffe4d4)', color: 'var(--brand)' }}>
          <HandCoins size={20} weight="bold" />
        </div>
        <div>
          <h2 style={S.modalTitle}>Desembolsar crédito</h2>
          <p style={S.modalSub}>
            Vamos a acreditar {fmtQ(solicitud.monto)} en la cuenta Neocoop del asociado.
          </p>
        </div>
      </div>

      <ResumenSolicitud s={solicitud} />

      <div style={{ ...S.banner('ok'), marginTop: 16 }}>
        <CheckCircle size={16} weight="bold" />
        <div>
          <div style={{ fontWeight: 600 }}>Pagaré firmado por el asociado</div>
          <div style={{ fontSize: 11, opacity: 0.85 }}>
            Firma capturada el {fmtFecha(solicitud.firma_timestamp)}
          </div>
        </div>
      </div>

      {pagareYaSubido ? (
        // Flujo nuevo · el asociado ya subió el PDF, el operador revisa antes de desembolsar
        <div style={{ margin: '18px 0 20px' }}>
          <div style={S.label}>Pagaré PDF firmado</div>
          <div style={{
            marginTop: 8,
            padding: '14px 16px',
            border: '1px solid var(--border)',
            borderRadius: 10,
            background: 'var(--bg-soft, #fafafa)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <FileText size={22} weight="bold" style={{ color: 'var(--brand)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Pagaré del asociado</div>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                Firmado electrónicamente desde la app · verificá el contenido antes de desembolsar
              </div>
            </div>
            <a
              href={previewUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { if (!previewUrl) e.preventDefault() }}
              style={{
                padding: '8px 14px',
                background: 'var(--brand)',
                color: '#fff',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                opacity: previewUrl ? 1 : 0.4,
                pointerEvents: previewUrl ? 'auto' : 'none',
              }}
            >
              {previewLoading ? 'Cargando…' : 'Abrir PDF'}
            </a>
          </div>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 14,
            padding: '10px 12px',
            background: confirmoRevision ? 'rgba(16,185,129,0.08)' : 'transparent',
            borderRadius: 8,
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={confirmoRevision}
              onChange={(e) => setConfirmoRevision(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
              Confirmo que revisé el pagaré PDF y los datos del asociado están correctos.
            </span>
          </label>
        </div>
      ) : (
        // El asociado firmó pero el PDF no se subió (falló la subida en la
        // app). Regeneramos el pagaré desde la firma que YA está en la DB.
        <div style={{ margin: '18px 0 20px' }}>
          <div style={{ ...S.hint, marginBottom: 10 }}>
            <Warning size={11} weight="bold" style={{ verticalAlign: 'middle', marginRight: 4 }} />
            El asociado firmó, pero el PDF del pagaré no se subió (falla de red en la app).
            Regeneralo acá desde la firma guardada.
          </div>
          <button
            onClick={onRegenerar}
            disabled={regenerando}
            style={{
              ...S.btnPrimary,
              width: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              opacity: regenerando ? 0.6 : 1,
              cursor: regenerando ? 'not-allowed' : 'pointer',
            }}
          >
            <ArrowsClockwise size={15} weight="bold" />
            {regenerando ? 'Regenerando pagaré…' : 'Regenerar pagaré desde la firma'}
          </button>
          {regenError && (
            <div style={{ ...S.banner('err'), marginTop: 10, fontSize: 12 }}>
              <Warning size={14} weight="bold" /> {regenError}
            </div>
          )}
          <button
            onClick={() => setMostrarManual((v) => !v)}
            style={{ background: 'none', border: 0, color: 'var(--muted-foreground)', fontSize: 11, cursor: 'pointer', marginTop: 10, padding: 0, textDecoration: 'underline' }}
          >
            {mostrarManual ? 'Ocultar opción manual' : '¿No funciona? Pegar URL manual'}
          </button>
          {mostrarManual && (
            <label style={{ display: 'block', marginTop: 8 }}>
              <input
                type="text"
                value={pagareUrlManual}
                onChange={(e) => setPagareUrlManual(e.target.value)}
                placeholder="asociadoId/solicitudId.pdf o URL completa"
                style={S.input}
              />
              <div style={S.hint}>Subí el PDF manual al bucket <code>pagares</code> y pegá el path.</div>
            </label>
          )}
        </div>
      )}

      <div style={S.modalActions}>
        <button onClick={onClose} disabled={busy} style={S.btnGhost}>
          Cancelar
        </button>
        <button
          onClick={() => onConfirm(solicitud, urlAEnviar)}
          disabled={busy || !canSubmit}
          style={{
            ...S.btnPrimary,
            opacity: canSubmit ? 1 : 0.55,
            cursor: busy || !canSubmit ? 'not-allowed' : 'pointer',
          }}
        >
          {busy ? 'Desembolsando…' : `Desembolsar ${fmtQ(solicitud.monto)}`}
        </button>
      </div>
    </ModalWrap>
  )
}

/* ═══════════════════════════════════════════════════════════════
   Tile de métrica para el header · lo que Hugo necesita ver primero
   ═══════════════════════════════════════════════════════════════ */

function MetricaTile({
  label, valor, detalle, tono,
}: {
  label: string
  valor: number | string
  detalle: string
  tono: 'warn' | 'neutral' | 'brand'
}) {
  const acento =
    tono === 'warn' ? '#EA580C'
    : tono === 'brand' ? '#0F172A'
    : '#64748B'
  return (
    <div style={{
      flex: '1 1 200px',
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 14,
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 4,
      minWidth: 0,
    }}>
      <div style={{
        fontSize: 11,
        color: 'var(--muted-foreground)',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
        fontWeight: 600,
      }}>{label}</div>
      <div style={{
        fontSize: 24,
        fontWeight: 800,
        color: acento,
        letterSpacing: -0.5,
      }}>{valor}</div>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{detalle}</div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════
   Card Hunter · una por solicitud
   ═══════════════════════════════════════════════════════════════ */

function SolicitudHunterCard({
  r, bandeja, busy, onAprobar, onRechazar, onCancelar, onDesembolsar, onVerificar,
}: {
  r: SolicitudRow
  bandeja: Bandeja
  busy: boolean
  onAprobar: () => void
  onRechazar: () => void
  onCancelar: () => void
  onDesembolsar: () => void
  onVerificar: () => void
}) {
  const verificado = !!r.otp_verificado_at
  const tier = r.tier_actual
  const tierLabel = tier ? TIER_LABEL[tier] : 'Sin tier'
  const tierColor = tier ? TIER_COLOR[tier] : '#94A3B8'
  const tierMonto = tier ? TIER_MONTO[tier] : 0
  const excedeMax = tier ? r.monto > tierMonto : false

  // Fecha e ISO relevante según bandeja
  const isoRelevante =
    bandeja === 'pendientes' ? r.created_at
    : bandeja === 'aprobadas_sin_firma' ? r.aprobada_at
    : r.firma_timestamp

  const iconoRelevante =
    bandeja === 'pendientes' ? 'Solicitado'
    : bandeja === 'aprobadas_sin_firma' ? 'Aprobado'
    : 'Firmado'

  return (
    <div style={hunterCardS.card}>
      {/* Header · tier badge + nombre + urgencia */}
      <div style={hunterCardS.header}>
        <div style={{ ...hunterCardS.tierBadge, background: tierColor }}>
          {tierLabel.slice(0, 3).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={hunterCardS.nombre}>{r.asociado_nombre}</div>
          <div style={hunterCardS.subLine}>
            neoID {r.asociado_neo_id} · Nivel {tierLabel}
          </div>
        </div>
        {(bandeja === 'pendientes' || bandeja === 'firmadas') && (
          <EsperaChip iso={isoRelevante} />
        )}
      </div>

      {/* Producto + monto */}
      <div style={hunterCardS.bodyRow}>
        <div style={hunterCardS.tipoNombre}>{r.tipo_nombre}</div>
        <div style={hunterCardS.montoRow}>
          <span style={hunterCardS.monto}>{fmtQ(r.monto)}</span>
          {tier && (
            <span style={{
              ...hunterCardS.montoMax,
              color: excedeMax ? '#DC2626' : 'var(--muted-foreground)',
            }}>
              de {fmtQ(tierMonto)} máx {tierLabel}
              {excedeMax && ' ⚠'}
            </span>
          )}
        </div>
        <div style={hunterCardS.plazoRow}>
          {r.plazo_dias} días
          {r.proposito && ` · ${r.proposito}`}
        </div>
      </div>

      {/* Contexto Hunter · retos + historial */}
      <div style={hunterCardS.hunterStrip}>
        {r.retos_activos > 0 ? (
          <span style={hunterCardS.chip}>
            🎯 {r.retos_activos} reto{r.retos_activos > 1 ? 's' : ''}
          </span>
        ) : (
          <span style={{ ...hunterCardS.chip, opacity: 0.5 }}>Sin retos</span>
        )}
        {r.creditos_previos === 0 ? (
          <span style={hunterCardS.chip}>1er crédito</span>
        ) : (
          <span style={hunterCardS.chip}>
            {r.creditos_previos + 1}º crédito · {r.creditos_al_dia} al día
            {r.creditos_en_mora > 0 && ` · ${r.creditos_en_mora} en mora`}
          </span>
        )}
        {r.aprobacion_auto && (
          <span style={{ ...hunterCardS.chip, background: 'rgba(16,185,129,0.08)', color: '#065F46', border: '1px solid rgba(16,185,129,0.28)' }}>
            Auto-aprobado
          </span>
        )}
        <span style={{
          ...hunterCardS.chip,
          background: verificado ? 'rgba(16,185,129,0.08)' : 'rgba(234,88,12,0.10)',
          color: verificado ? '#065F46' : '#9A3412',
          border: `1px solid ${verificado ? 'rgba(16,185,129,0.28)' : 'rgba(234,88,12,0.28)'}`,
        }}>
          {verificado ? '✓ Código verificado' : '⚠ Falta código del asociado'}
        </span>
      </div>
      {!verificado && (
        <button
          onClick={onVerificar}
          disabled={busy}
          style={{
            marginTop: 8, width: '100%', padding: '8px 10px', borderRadius: 8,
            border: '1px solid rgba(234,88,12,0.35)', background: 'rgba(234,88,12,0.06)',
            color: '#9A3412', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Override: verificar sin código (si no le llegó)
        </button>
      )}

      {/* Fecha */}
      <div style={hunterCardS.fecha}>
        {iconoRelevante} · {fmtFecha(isoRelevante)}
      </div>

      {/* Acciones */}
      <div style={hunterCardS.actions}>
        {bandeja === 'pendientes' && (
          <>
            <ActionBtn
              onClick={onRechazar}
              disabled={busy}
              tono="danger"
              icon={<XCircle size={14} weight="bold" />}
              label="Rechazar"
            />
            <ActionBtn
              onClick={onAprobar}
              disabled={busy}
              tono="success"
              icon={<CheckCircle size={14} weight="bold" />}
              label="Aprobar"
            />
          </>
        )}
        {bandeja === 'aprobadas_sin_firma' && (
          <>
            <span style={{ fontSize: 12, color: 'var(--muted-foreground)', flex: 1 }}>
              Esperando firma del asociado en la app
            </span>
            <ActionBtn
              onClick={onCancelar}
              disabled={busy}
              tono="danger"
              icon={<XCircle size={14} weight="bold" />}
              label="Cancelar"
            />
          </>
        )}
        {bandeja === 'firmadas' && (
          <>
            <ActionBtn
              onClick={onCancelar}
              disabled={busy}
              tono="danger"
              icon={<XCircle size={14} weight="bold" />}
              label="Cancelar"
            />
            {r.desembolso_programado_at && !requiereDesembolsoManual(r) ? (
              // Flujo normal · el cron va a desembolsar solo. Sin botón de acción.
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-soft)',
                textAlign: 'right',
              }}>
                ⏳ Desembolso automático programado · {fmtHora(r.desembolso_programado_at)}
              </span>
            ) : r.desembolso_programado_at ? (
              // Fallback · la hora programada ya pasó y el cron no la agarró.
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <ActionBtn
                  onClick={onDesembolsar}
                  disabled={busy}
                  tono="primary"
                  icon={<HandCoins size={14} weight="bold" />}
                  label="Desembolsar ahora (fallback)"
                />
                <span style={{ fontSize: 11, color: 'var(--muted-foreground)', textAlign: 'right' }}>
                  El desembolso automático no corrió. Podés forzarlo.
                </span>
              </div>
            ) : (
              // Legacy · solicitud vieja sin desembolso programado. Desembolso manual como antes.
              <ActionBtn
                onClick={onDesembolsar}
                disabled={busy}
                tono="primary"
                icon={<HandCoins size={14} weight="bold" />}
                label="Desembolsar"
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

const hunterCardS = {
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 18,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 14,
    boxShadow: 'var(--shadow-sm)',
    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  } as React.CSSProperties,
  tierBadge: {
    minWidth: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.5,
  } as React.CSSProperties,
  nombre: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--ink)',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
  } as React.CSSProperties,
  subLine: {
    fontSize: 11,
    color: 'var(--muted-foreground)',
    marginTop: 2,
  } as React.CSSProperties,
  bodyRow: {
    paddingTop: 4,
    borderTop: '1px solid var(--hairline, rgba(0,0,0,0.06))',
  } as React.CSSProperties,
  tipoNombre: {
    fontSize: 12,
    color: 'var(--muted-foreground)',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    fontWeight: 600,
    marginTop: 10,
    marginBottom: 6,
  } as React.CSSProperties,
  montoRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  monto: {
    fontSize: 24,
    fontWeight: 800,
    color: 'var(--ink)',
    letterSpacing: -0.5,
  } as React.CSSProperties,
  montoMax: {
    fontSize: 11,
  } as React.CSSProperties,
  plazoRow: {
    fontSize: 12,
    color: 'var(--muted-foreground)',
    marginTop: 6,
  } as React.CSSProperties,
  hunterStrip: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
  } as React.CSSProperties,
  chip: {
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    background: 'rgba(100,116,139,0.08)',
    color: '#475569',
    border: '1px solid rgba(100,116,139,0.16)',
  } as React.CSSProperties,
  fecha: {
    fontSize: 11,
    color: 'var(--muted-foreground)',
  } as React.CSSProperties,
  actions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: 4,
  } as React.CSSProperties,
}

/* ═══════════════════════════════════════════════════════════════
   Estilos · design tokens Neocoop
   ═══════════════════════════════════════════════════════════════ */

const S = {
  page: {
    padding: '24px 32px',
    maxWidth: 1280,
    margin: '0 auto',
    background: 'var(--bg)',
    minHeight: '100%',
    fontFamily: 'var(--font-sans, inherit)',
  } as React.CSSProperties,

  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 20,
    marginBottom: 20,
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  crumb: {
    fontSize: 13,
    color: 'var(--ink-soft)',
    textDecoration: 'none',
    fontWeight: 500,
  } as React.CSSProperties,

  h1: {
    fontSize: 26,
    fontWeight: 700,
    color: 'var(--ink)',
    margin: 0,
    letterSpacing: '-0.02em',
  } as React.CSSProperties,

  subtitle: {
    fontSize: 14,
    color: 'var(--ink-soft)',
    margin: '6px 0 0',
    maxWidth: 620,
  } as React.CSSProperties,

  tabs: {
    display: 'flex',
    gap: 4,
    marginBottom: 20,
    borderBottom: '1px solid var(--border)',
    overflowX: 'auto' as const,
  } as React.CSSProperties,

  banner: (tono: 'ok' | 'err' | 'warn') => {
    const map = {
      ok:   { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.30)', color: '#065F46' },
      err:  { bg: 'rgba(163,45,45,0.08)',  border: 'rgba(163,45,45,0.30)',  color: 'var(--danger)' },
      warn: { bg: 'rgba(217,119,6,0.08)',  border: 'rgba(217,119,6,0.30)',  color: 'var(--warning)' },
    }[tono]
    return {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      marginBottom: 16,
      background: map.bg,
      border: `1px solid ${map.border}`,
      borderRadius: 'var(--radius-md, 10px)',
      color: map.color,
      fontSize: 13,
    } as React.CSSProperties
  },

  emptyPanel: {
    textAlign: 'center' as const,
    padding: '56px 24px',
    background: 'var(--surface)',
    border: '1px dashed var(--border-strong)',
    borderRadius: 'var(--radius-lg, 14px)',
  } as React.CSSProperties,

  tableCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg, 14px)',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-sm)',
  } as React.CSSProperties,

  // Grid Hunter · 2 columnas en desktop, 1 en mobile · gap generoso.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
    gap: 16,
    marginTop: 4,
  } as React.CSSProperties,

  // Row de métricas del header · flex wrap con gap
  metricasRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 12,
    marginBottom: 20,
  } as React.CSSProperties,

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  } as React.CSSProperties,

  trHead: {
    background: 'var(--soft-gray, #f6f7f9)',
  } as React.CSSProperties,

  trBody: {
    borderBottom: '1px solid var(--border)',
  } as React.CSSProperties,

  tagInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: 'var(--ink-soft)',
    background: 'var(--soft-gray, #f6f7f9)',
    padding: '4px 10px',
    borderRadius: 999,
    fontWeight: 500,
  } as React.CSSProperties,

  btnGhost: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 14px',
    fontSize: 13,
    fontWeight: 500,
    background: 'var(--surface)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-md, 10px)',
    color: 'var(--ink)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  } as React.CSSProperties,

  btnPrimary: {
    padding: '10px 20px',
    fontSize: 13,
    fontWeight: 600,
    background: 'var(--brand)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md, 10px)',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-brand-sm)',
    fontFamily: 'inherit',
  } as React.CSSProperties,

  modalBackdrop: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(20,23,28,0.42)',
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backdropFilter: 'blur(2px)',
  } as React.CSSProperties,

  modalCard: {
    background: 'var(--surface)',
    borderRadius: 'var(--radius-xl, 18px)',
    padding: 24,
    maxWidth: 520,
    width: '100%',
    boxShadow: 'var(--shadow-lg)',
    fontFamily: 'var(--font-sans, inherit)',
  } as React.CSSProperties,

  modalHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  } as React.CSSProperties,

  modalIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  } as React.CSSProperties,

  modalTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--ink)',
    letterSpacing: '-0.01em',
  } as React.CSSProperties,

  modalSub: {
    margin: '4px 0 0',
    fontSize: 12,
    color: 'var(--ink-soft)',
    lineHeight: 1.5,
  } as React.CSSProperties,

  resumenGrid: {
    background: 'var(--soft-gray, #f6f7f9)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md, 10px)',
    padding: 14,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  } as React.CSSProperties,

  label: {
    display: 'block',
    fontSize: 12,
    color: 'var(--ink-soft)',
    marginBottom: 6,
    fontWeight: 600,
  } as React.CSSProperties,

  textarea: {
    width: '100%',
    padding: 10,
    fontSize: 13,
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-md, 10px)',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    color: 'var(--ink)',
    background: 'var(--surface)',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  input: {
    width: '100%',
    padding: 10,
    fontSize: 13,
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-md, 10px)',
    fontFamily: 'inherit',
    color: 'var(--ink)',
    background: 'var(--surface)',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  hint: {
    fontSize: 11,
    color: 'var(--muted-foreground)',
    marginTop: 6,
    lineHeight: 1.4,
  } as React.CSSProperties,

  modalActions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  } as React.CSSProperties,

  toast: {
    position: 'fixed' as const,
    top: 88,
    right: 24,
    maxWidth: 380,
    padding: '12px 14px',
    borderRadius: 'var(--radius-md, 10px)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 100,
    animation: 'slideInRight 200ms ease',
  } as React.CSSProperties,

  toastOk: {
    background: '#0f8a57',
    color: '#fff',
    border: '1px solid #086a42',
  } as React.CSSProperties,

  toastErr: {
    background: '#a32d2d',
    color: '#fff',
    border: '1px solid #7a2222',
  } as React.CSSProperties,
}
